// Field-level Special Personal Information classification (modules-and-api.md §2.2(c); TEN-66, TEN-72, R-02, R-04).
//
// One declaration per response schema, five enforcement points: the serialiser (`redactFor`), the audit differ,
// the log redactor, the Analytics Engine event builder and the CRM payload builder (`classifiedFields`).
// Redaction is by omission with an explicit marker — the field becomes `null` and its name is listed in
// `_redacted` — so a client renders "restricted" rather than "empty" (a hidden clinical field that looks empty
// is a patient-safety problem, not a UI nicety).

import type { z } from "zod";
import type { ExtendedRole } from "./permissions";

export const SPI_CLASSES = ["health", "identity", "financial"] as const;
export type SpiClass = (typeof SPI_CLASSES)[number];

/** A background job or integration. It sees only the classifications its job declares (§2.2(c), R-17). */
export interface SystemPrincipal {
  readonly kind: "system";
  /** `system:<job>` as written to the audit row (TEN-63). */
  readonly job: string;
  readonly declares?: readonly SpiClass[];
}
/** A role, the bare `"system"` kind (declares nothing), or a system principal with declarations. */
export type SpiPrincipal = ExtendedRole | "system" | SystemPrincipal;

/**
 * Which classifications each role never sees, whatever the route granted.
 * - Receptionist: demographics and appointments, no `health` (R-04, TEN-66).
 * - FrameConsultant: reads prescriptions for dispensing — a route grant, not a patient-record field — and
 *   otherwise no `health` (TEN-66 "Frame Consultant read-only prescriptions needed for dispensing").
 * - FranchisorAdmin: reports, logs and user admin only; no `health`, no `identity` (TEN-08, R-02).
 * - PlatformAdmin: reaches tenant data only under a SupportAccessGrant; the same minimisation applies (R-22).
 * Nobody is denied `financial` by role — it exists for system principals and the audit/CRM builders.
 */
export const SPI_DENIED_BY_ROLE: Readonly<Record<ExtendedRole, readonly SpiClass[]>> = {
  Owner: [],
  Admin: [],
  Optometrist: [],
  Dispenser: [],
  FrameConsultant: ["health"],
  Receptionist: ["health"],
  FranchisorAdmin: ["health", "identity"],
  PlatformAdmin: ["health", "identity"],
};

/** The classifications a principal must not see. An unknown role is treated as seeing nothing classified. */
export function deniedClasses(principal: SpiPrincipal): ReadonlySet<SpiClass> {
  if (principal === "system") return new Set(SPI_CLASSES);
  if (typeof principal === "object") {
    const declared = new Set(principal.declares ?? []);
    return new Set(SPI_CLASSES.filter((c) => !declared.has(c)));
  }
  const denied = SPI_DENIED_BY_ROLE[principal] as readonly SpiClass[] | undefined;
  return new Set(denied ?? SPI_CLASSES);
}

type ObjectSchema = z.ZodObject;
export type Classification<T extends ObjectSchema> = Partial<Record<keyof T["shape"] & string, SpiClass>>;

/** The value with every denied classified field set to `null` and named in `_redacted`. */
export type Redacted<V, C extends PropertyKey> = Omit<V, C> & { [K in Extract<C, keyof V>]: V[K] | null } & {
  _redacted: string[];
};

export interface SpiSchema<T extends ObjectSchema, F extends Classification<T>> {
  readonly schema: T;
  /** Field → classification, in declaration order. */
  readonly fields: Readonly<F>;
  /** The redactor for one principal — apply it to every value of this schema in the response. */
  redactFor(principal: SpiPrincipal): (value: z.output<T>) => Redacted<z.output<T>, keyof F>;
  /** One-shot form of `redactFor`. */
  redact(value: z.output<T>, principal: SpiPrincipal): Redacted<z.output<T>, keyof F>;
  /** The field names this principal loses, in declaration order. */
  redactedFieldsFor(principal: SpiPrincipal): string[];
}

const CLASSIFICATIONS = new WeakMap<object, Readonly<Record<string, SpiClass>>>();

/**
 * Declare the Special Personal Information fields of a response schema once.
 *
 * ```ts
 * const PatientView = spi(PatientSchema, { medicalHistory: 'health', idNumber: 'identity' });
 * const body = PatientView.redactFor(ctx.role)(patient);   // { ..., medicalHistory: null, _redacted: ['medicalHistory'] }
 * ```
 * Throws at declaration when a classified field is not in the schema — a typo here would silently widen reach.
 */
export function spi<T extends ObjectSchema, const F extends Classification<T>>(schema: T, fields: F): SpiSchema<T, F> {
  const shape = schema.shape as Record<string, unknown>;
  const entries = Object.entries(fields).filter((e): e is [string, SpiClass] => e[1] !== undefined);
  for (const [field, cls] of entries) {
    if (!(field in shape)) throw new Error(`spi: field "${field}" is not in the schema`);
    if (!SPI_CLASSES.includes(cls)) throw new Error(`spi: "${field}" has unknown classification "${String(cls)}"`);
  }
  const table: Readonly<Record<string, SpiClass>> = Object.freeze(Object.fromEntries(entries));
  CLASSIFICATIONS.set(schema, table);

  const redactedFieldsFor = (principal: SpiPrincipal): string[] => {
    const denied = deniedClasses(principal);
    return entries.filter(([, cls]) => denied.has(cls)).map(([field]) => field);
  };
  const redactFor = (principal: SpiPrincipal) => {
    const drop = redactedFieldsFor(principal);
    return (value: z.output<T>): Redacted<z.output<T>, keyof F> => {
      const out: Record<string, unknown> = { ...(value as Record<string, unknown>) };
      for (const field of drop) out[field] = null;
      out._redacted = [...drop];
      return out as Redacted<z.output<T>, keyof F>;
    };
  };
  const wrapper: SpiSchema<T, F> = {
    schema,
    fields,
    redactFor,
    redact: (value, principal) => redactFor(principal)(value),
    redactedFieldsFor,
  };
  CLASSIFICATIONS.set(wrapper, table);
  return wrapper;
}

/**
 * The classification table of a schema (or of its `spi()` wrapper) — empty when nothing is classified.
 * Used by the audit differ, the log redactor, the Analytics Engine event builder and the CRM payload builder
 * to exclude `health` and `identity` fields by the same declaration the serialiser uses (TEN-72, rule 44).
 */
export function classifiedFields(schemaOrWrapper: object): Readonly<Record<string, SpiClass>> {
  return CLASSIFICATIONS.get(schemaOrWrapper) ?? {};
}

/** Field names of a schema carrying any of the given classifications (default: `health` and `identity`, the TEN-72 set). */
export function fieldsClassifiedAs(schemaOrWrapper: object, classes: readonly SpiClass[] = ["health", "identity"]): string[] {
  const wanted = new Set(classes);
  return Object.entries(classifiedFields(schemaOrWrapper))
    .filter(([, cls]) => wanted.has(cls))
    .map(([field]) => field);
}
